import { Endpoints } from "@/shared/API/APIConfig";
import { baseApi, type ApiResponse, type ApiSuccessResponse } from "./base.api";
import {
  mapLiveInventoryResponse,
  mapOpeningBalanceResponse,
} from "./mappers/inventory.mapper";
import type {
  LiveInventoryFilters,
  LiveInventoryResponse,
  OpeningBalanceItem,
  ApiInventoryHelpDataResponse,
  ApiInventoryMovementHistoryHelpDataResponse,
  ApiLiveInventoryResponse,
  ApiOpeningBalanceResponse,
  OpeningBalanceResponse,
  OpeningBalanceParams,
  StockDetailsResponse,
  MovementHistory,
} from "@/app/types/inventory.types";
import type { OpeningBalancePayload } from "@/features/Inventory/components/types/OpeningBalanceTypes";

export const inventoryApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getInventoryHelpData: builder.query<ApiInventoryHelpDataResponse, void>({
      query: () => Endpoints.inventory.getHelpData,
      transformResponse: (
        response: ApiResponse<ApiInventoryHelpDataResponse>,
      ): ApiInventoryHelpDataResponse => {
        if (!response.success) {
          throw new Error(
            response.message || "Failed to load inventory helper data",
          );
        }
        return response.data;
      },
      providesTags: ["Inventory"],
    }),
    getInventoryMovementHistoryHelpData: builder.query<
      ApiInventoryMovementHistoryHelpDataResponse,
      void
    >({
      query: () => Endpoints.inventory.getHelpDataMovementHistory,
      transformResponse: (
        response: ApiSuccessResponse<ApiInventoryMovementHistoryHelpDataResponse>,
      ): ApiInventoryMovementHistoryHelpDataResponse => {
        if (!response.success) {
          throw new Error(
            response.message || "Failed to load inventory movement history helper data",
          );
        }
        return response.data;
      },
      providesTags: ["Inventory"],
    }),
    getMovementHistory: builder.query<
      MovementHistory[],
      {
        item_id: string | number;
        last_days: number;
        movement_type?: number;
        sort_by?: string;
        sort_dirt?: "asc" | "desc";
      }
    >({
      query: ({ item_id, last_days, movement_type }) => ({
        url: Endpoints.inventory.getMovementHistory,
        method: "GET",
        params: {
          last_days,
          movement_type,
          item_id,
          sort_by: "created_at",
          sort_dir: "desc",
        },
      }),
      transformResponse: (
        response: ApiResponse<MovementHistory[]>,
      ): MovementHistory[] => {
        if (!response.success) {
          throw new Error(
            response.message || "Failed to load inventory helper data",
          );
        }
        return response.data;
      },
      providesTags: ["Inventory"],
    }),
    getLiveInventory: builder.query<
      LiveInventoryResponse,
      LiveInventoryFilters
    >({
      query: ({
        page = 1,
        per_page = 10,
        search,
        category_id,
        subcategory_ids,
        inventory_status_id,
      }) => {
        const queryParams = new URLSearchParams();
        queryParams.set("page", String(page));
        queryParams.set("per_page", String(per_page));

        if (search?.trim()) queryParams.set("search", search.trim());
        if (category_id) queryParams.set("category_id", category_id);
        if (subcategory_ids?.length) {
          subcategory_ids.forEach((id, index) => {
            queryParams.set(`subcategory_ids[${index}]`, id);
          });
        }
        if (inventory_status_id) {
          queryParams.set("inventory_status", String(inventory_status_id));
        }

        return `${Endpoints.inventory.getLiveInventory}?${queryParams.toString()}`;
      },
      transformResponse: (
        response: ApiResponse<ApiLiveInventoryResponse>,
      ): LiveInventoryResponse => {
        if (!response.success) {
          throw new Error(response.message || "Failed to load live inventory");
        }
        return mapLiveInventoryResponse(response.data);
      },
      providesTags: ["Inventory"],
      keepUnusedDataFor: 0,
    }),
    getOpeningBalance: builder.query<OpeningBalanceItem[], void>({
      query: () => Endpoints.inventory.getOpeningBalance,
      transformResponse: (
        response: ApiResponse<ApiOpeningBalanceResponse>,
      ): OpeningBalanceItem[] => {
        if (!response.success) {
          throw new Error(response.message || "Failed to load opening balance");
        }
        return mapOpeningBalanceResponse(response.data);
      },
      providesTags: ["Inventory"],
    }),
    getAllItems: builder.infiniteQuery<
      OpeningBalanceResponse,
      OpeningBalanceParams,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          const totalPages: number = lastPage?.data?.total_pages ?? 1;
          return lastPageParam < totalPages ? lastPageParam + 1 : undefined;
        },
      },
      query({ queryArg, pageParam }) {
        const queryParams = new URLSearchParams();
        queryParams.set("page", String(pageParam));
        queryParams.set("per_page", "10");

        if (queryArg.search?.trim())
          queryParams.set("search", queryArg.search.trim());
        if (queryArg.category_ids?.length) {
          queryArg.category_ids.forEach((id: string, index: number) => {
            queryParams.set(`category_ids[${index}]`, id);
          });
        }

        return `${Endpoints.inventory.getAllItems}?${queryParams.toString()}`;
      },
      transformResponse: (
        response: ApiResponse<OpeningBalanceResponse>,
      ): OpeningBalanceResponse => {
        if (!response.success) {
          throw new Error(response.message || "Failed to load items");
        }
        return response.data;
      },
      providesTags: ["Inventory"],
    }),
    setOpeningBalance: builder.mutation<void, OpeningBalancePayload>({
      query: (payload) => ({
        url: Endpoints.inventory.setOpeningBalance,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Inventory"],
    }),
    deleteOpeningBalanceDraft: builder.mutation<void, void>({
      query: () => {
        return {
          url: Endpoints.inventory.deleteOpeningBalanceDraft,
          method: "DELETE",
        };
      },

      invalidatesTags: ["Inventory"],
    }),
    getStockDetails: builder.query<StockDetailsResponse, string>({
      query: (id: string | number) => Endpoints.inventory.getStockDetails(id),
      transformResponse: (
        response: ApiResponse<StockDetailsResponse>,
      ): StockDetailsResponse => {
        if (!response.success) {
          throw new Error(
            response.message || "Failed to load inventory helper data",
          );
        }
        return response.data;
      },
      providesTags: ["Inventory"],
    }),
  }),
});

export const {
  useGetInventoryHelpDataQuery,
  useGetInventoryMovementHistoryHelpDataQuery,
  useGetLiveInventoryQuery,
  useLazyGetOpeningBalanceQuery,
  useGetAllItemsInfiniteQuery,
  useSetOpeningBalanceMutation,
  useDeleteOpeningBalanceDraftMutation,
  useGetStockDetailsQuery,
  useGetMovementHistoryQuery,
} = inventoryApi;
